import React, { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { CityRadiusSelectorProps, SelectedLocation } from "@/types/geoTargeting";

export function CityRadiusSelector({ location, onChange, onClose, isSpecialAdCategory }: CityRadiusSelectorProps) {
  // For SAC: radius is mandatory (15-50mi / 25-80km)
  // For Standard: radius is optional, but if set must be 10-50mi / 17-80km
  const [useRadius, setUseRadius] = useState(isSpecialAdCategory ? true : Boolean(location.radius));
  const [radius, setRadius] = useState(location.radius || (isSpecialAdCategory ? 15 : 25));
  const [unit, setUnit] = useState<"mile" | "kilometer">(location.distance_unit || "mile");

  // Radius constraints
  const getMinRadius = (unitType: "mile" | "kilometer") => {
    if (isSpecialAdCategory) {
      return unitType === "mile" ? 15 : 25;
    }
    return unitType === "mile" ? 10 : 17;
  };

  const getMaxRadius = (unitType: "mile" | "kilometer") => {
    return unitType === "mile" ? 50 : 80;
  };

  const minRadius = getMinRadius(unit);
  const maxRadius = getMaxRadius(unit);

  const handleUnitChange = (newUnit: "mile" | "kilometer") => {
    setUnit(newUnit);
    const newMin = getMinRadius(newUnit);
    const newMax = getMaxRadius(newUnit);

    // Clamp radius to new valid range
    if (radius > newMax) {
      setRadius(newMax);
    } else if (radius < newMin) {
      setRadius(newMin);
    }
  };

  const handleUseRadiusChange = (checked: boolean) => {
    if (isSpecialAdCategory) {
      // SAC always requires radius, don't allow disabling
      return;
    }
    setUseRadius(checked);
    if (checked && !radius) {
      setRadius(25); // Default radius when enabling
    }
  };

  const handleApply = () => {
    const updated: SelectedLocation = {
      ...location,
    };

    if (useRadius) {
      updated.radius = radius;
      updated.distance_unit = unit;
    } else {
      // Remove radius if not using it (standard campaigns only)
      delete updated.radius;
      delete updated.distance_unit;
    }

    onChange(updated);
  };

  const locationType = location.type === "zip" ? "zip code" : "city";

  return (
    <div className="absolute left-0 top-full mt-2 z-50 w-80 rounded-md border bg-popover p-4 shadow-md">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-sm">Radius for {location.name}</h4>
        <button type="button" onClick={onClose} className="p-1 rounded-full hover:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        {/* Toggle for using radius (only for standard campaigns) */}
        {!isSpecialAdCategory && (
          <div className="flex items-center justify-between">
            <Label htmlFor="use-radius" className="text-sm">
              Use radius targeting
            </Label>
            <Switch id="use-radius" checked={useRadius} onCheckedChange={handleUseRadiusChange} />
          </div>
        )}

        {/* Radius controls - shown when radius is enabled */}
        {useRadius && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Radius</Label>
                <span className="text-sm font-medium">
                  {radius} {unit === "kilometer" ? "km" : "mi"}
                </span>
              </div>
              <Slider
                value={[radius]}
                onValueChange={([val]) => setRadius(val)}
                min={minRadius}
                max={maxRadius}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {minRadius} {unit === "kilometer" ? "km" : "mi"}
                </span>
                <span>
                  {maxRadius} {unit === "kilometer" ? "km" : "mi"}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Unit</Label>
              <Select value={unit} onValueChange={(v) => handleUnitChange(v as "mile" | "kilometer")}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mile">Miles</SelectItem>
                  <SelectItem value="kilometer">Kilometers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {/* Info text */}
        <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
          {isSpecialAdCategory ? (
            <span className="text-amber-600">
              Special Ad Category requires {locationType} targeting with 15-50 mile (25-80 km) radius.
            </span>
          ) : useRadius ? (
            <span>Standard campaigns allow {locationType} targeting with 10-50 mile (17-80 km) radius.</span>
          ) : (
            <span>Target the exact {locationType} location without a surrounding radius.</span>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply} className="flex-1">
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
